import React, { useState, useEffect } from 'react';
import { useSearchParams, Link as RouterLink } from 'react-router-dom';
import {
  Box, TextField, Button, Paper, Typography, CircularProgress,
  List, ListItem, ListItemText, Divider, Table, TableBody,
  TableRow, TableCell, Chip, Grid, Collapse, ToggleButton,
  ToggleButtonGroup, FormControl, InputLabel, Select, MenuItem,
  Pagination, Accordion, AccordionSummary, AccordionDetails, Checkbox,
  Autocomplete
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import TuneIcon from '@mui/icons-material/Tune';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SortIcon from '@mui/icons-material/Sort';
import { searchBills } from '../services/api';
import DateHierarchyFilter from './DateHierarchyFilter';
import PageSizeSelector, { DEFAULT_PAGE_SIZE } from './PageSizeSelector';
import ExportCSVButton from './ExportCSVButton';
import RSSButton from './RSSButton';
import { SaveSearchButton } from './saved-searches';

// #251: explicit filter-rail ordering (was sortFacetsBooleansFirst, which
// promoted boolean-shaped facets to the top and forced statuses /
// originating_chambers / bill_types open). Date is rendered above this list
// by DateHierarchyFilter.
const FACET_ORDER = [
  'originating_chambers',
  'bill_types',
  'portfolios',
  'parliament_numbers',
  'sponsors',
  'statuses',
];

// #251: sort options. Default = Date (newest) — last_updated:desc.
const DEFAULT_SORT = 'last_updated:desc';
const SORT_OPTIONS = [
  { value: '_score', label: 'Relevance' },
  { value: 'last_updated:desc', label: 'Date (newest)' },
  { value: 'last_updated:asc', label: 'Date (oldest)' },
  { value: 'title.keyword:asc', label: 'Title (A-Z)' },
  { value: 'title.keyword:desc', label: 'Title (Z-A)' },
];

export default function BillsSearch() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchMode, setSearchMode] = useState(searchParams.get('mode') || 'simple');
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedDetails, setExpandedDetails] = useState({});
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(() => {
    const urlPageSize = parseInt(searchParams.get('pageSize'), 10);
    return [10, 25, 50, 100].includes(urlPageSize) ? urlPageSize : DEFAULT_PAGE_SIZE;
  });
  const [activeFilters, setActiveFilters] = useState({});
  const [facets, setFacets] = useState({});
  // #251: sort state. URL param `sort` survives page reloads.
  const [sortBy, setSortBy] = useState(() => {
    const urlSort = searchParams.get('sort');
    return SORT_OPTIONS.some(o => o.value === urlSort) ? urlSort : DEFAULT_SORT;
  });
  // #251: canonical-chamber filter (separate from activeFilters because it
  // travels as the dedicated `chamber=house|senate` API param, not a Lucene
  // query-string clause — so it gets backend variant-expansion).
  const [chamberFilter, setChamberFilter] = useState(null);

  // Date drill-down state
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);

  // Advanced form state
  const [form, setForm] = useState({
    keywords: '',
    status: '',
    originatingChamber: '',
    billType: '',
    portfolio: '',
    sponsor: '',
    parliamentNumber: '',
    mover: ''
  });

  // Options for autocomplete fields
  const [availableOptions, setAvailableOptions] = useState({
    portfolios: [],
    sponsors: [],
    movers: [],
    parliamentNumbers: []
  });

  // Fetch autocomplete options on mount
  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const data = await searchBills('*', 1, 100);
        if (data.hits) {
          const portfolios = [...new Set(data.hits.map(h => h.portfolio).filter(Boolean))].sort();
          const sponsors = [...new Set(data.hits.flatMap(h => h.sponsors || []).filter(Boolean))].sort();
          const movers = [...new Set(data.hits.flatMap(h => h.movers || []).filter(Boolean))].sort();
          const parliamentNumbers = [...new Set(data.hits.map(h => h.parliament_number).filter(Boolean))].sort((a, b) => b - a);
          setAvailableOptions({ portfolios, sponsors, movers, parliamentNumbers });
        }
      } catch (error) {
        console.error('Failed to fetch options:', error);
      }
    };
    fetchOptions();
  }, []);

  useEffect(() => {
    const urlQuery = searchParams.get('q');
    if (urlQuery && urlQuery !== '*') {
      handleSearch(urlQuery);
    } else {
      handleSearch('*');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (field) => (event) => {
    setForm({ ...form, [field]: event.target.value });
  };

  const buildAdvancedQuery = () => {
    const parts = [];

    if (form.keywords) parts.push(`(${form.keywords})`);
    if (form.status) parts.push(`status:"${form.status}"`);
    if (form.originatingChamber) parts.push(`originating_chamber:"${form.originatingChamber}"`);
    if (form.billType) parts.push(`bill_type:"${form.billType}"`);
    if (form.portfolio) parts.push(`portfolio:"${form.portfolio}"`);
    if (form.sponsor) parts.push(`sponsors:"${form.sponsor}"`);
    if (form.parliamentNumber) parts.push(`parliament_number:${form.parliamentNumber}`);
    if (form.mover) parts.push(`movers:"${form.mover}"`);

    return parts.length > 0 ? parts.join(' AND ') : '*';
  };

  const buildQueryWithFilters = (baseQuery, filters) => {
    const filterParts = [];
    Object.entries(filters).forEach(([field, values]) => {
      if (values.length > 0) {
        const fieldQuery = values.map(v => `${field}:"${v}"`).join(' OR ');
        filterParts.push(`(${fieldQuery})`);
      }
    });

    if (filterParts.length === 0) return baseQuery;
    if (baseQuery === '*') return filterParts.join(' AND ');
    return `(${baseQuery}) AND ${filterParts.join(' AND ')}`;
  };

  const handleSearch = async (
    searchQuery = null,
    newPage = 1,
    filters = activeFilters,
    year = selectedYear,
    month = selectedMonth,
    pageSize = perPage,
    sort = sortBy,
    chamber = chamberFilter,
  ) => {
    let baseQuery;
    if (searchMode === 'simple') {
      baseQuery = searchQuery !== null ? searchQuery : (query || '*');
    } else {
      baseQuery = buildAdvancedQuery();
    }

    const q = buildQueryWithFilters(baseQuery, filters);

    setLoading(true);
    try {
      // #251: chamber travels via the dedicated `chamber` param (with
      // backend authority-map expansion); sort uses field:dir.
      const apiFilters = chamber ? { chamber } : {};
      const data = await searchBills(q, newPage, pageSize, year, month, sort, apiFilters);
      setResults(data);
      setPage(newPage);
      if (data.facets) {
        setFacets(data.facets);
      }
      const newParams = { q: baseQuery, mode: searchMode };
      if (pageSize !== DEFAULT_PAGE_SIZE) newParams.pageSize = pageSize;
      if (sort && sort !== DEFAULT_SORT) newParams.sort = sort;
      setSearchParams(newParams);
    } catch (error) {
      console.error('Search error:', error);
    }
    setLoading(false);
  };

  const handlePageSizeChange = (newSize) => {
    setPerPage(newSize);
    handleSearch(null, 1, activeFilters, selectedYear, selectedMonth, newSize);
  };

  const handleSortChange = (newSort) => {
    setSortBy(newSort);
    handleSearch(null, 1, activeFilters, selectedYear, selectedMonth, perPage, newSort);
  };

  // Date drill-down handlers
  const handleYearClick = (year) => {
    setSelectedYear(year);
    setSelectedMonth(null);
    setSelectedDay(null);
    handleSearch(null, 1, activeFilters, year, null);
  };

  const handleMonthClick = (month) => {
    setSelectedMonth(month);
    setSelectedDay(null);
    const year = month.split('-')[0];
    setSelectedYear(year);
    handleSearch(null, 1, activeFilters, year, month);
  };

  const handleDayClick = (day) => {
    setSelectedDay(day);
  };

  const clearDateDrillDown = () => {
    setSelectedYear(null);
    setSelectedMonth(null);
    setSelectedDay(null);
    handleSearch(null, 1, activeFilters, null, null);
  };

  const handleFacetClick = (field, value) => {
    const newFilters = { ...activeFilters };
    if (!newFilters[field]) newFilters[field] = [];
    const valueIndex = newFilters[field].indexOf(value);
    if (valueIndex > -1) {
      newFilters[field] = newFilters[field].filter(v => v !== value);
      if (newFilters[field].length === 0) delete newFilters[field];
    } else {
      newFilters[field].push(value);
    }
    setActiveFilters(newFilters);
    handleSearch(null, 1, newFilters);
  };

  // #251: chamber facet uses the canonical chamber param so the backend
  // expands across all 4 variants. Single-select (radio behaviour).
  const CHAMBER_LABEL_TO_ID = {
    'House of Representatives': 'house',
    'Senate': 'senate',
  };
  const handleChamberFacetClick = (label) => {
    const id = CHAMBER_LABEL_TO_ID[label];
    if (!id) return;
    const next = chamberFilter === id ? null : id;
    setChamberFilter(next);
    handleSearch(null, 1, activeFilters, selectedYear, selectedMonth, perPage, sortBy, next);
  };

  const clearAllFilters = () => {
    setActiveFilters({});
    setChamberFilter(null);
    handleSearch(null, 1, {}, selectedYear, selectedMonth, perPage, sortBy, null);
  };

  const facetToField = (facetName) => {
    const mapping = {
      'statuses': 'status',
      'originating_chambers': 'originating_chamber',
      'bill_types': 'bill_type',
      'portfolios': 'portfolio',
      'parliament_numbers': 'parliament_number',
      'sponsors': 'sponsors'
    };
    return mapping[facetName] || facetName;
  };

  const facetDisplayName = (facetName) => {
    const names = {
      'statuses': 'Status',
      'originating_chambers': 'Chamber',
      'bill_types': 'Bill Type',
      'portfolios': 'Portfolio',
      'parliament_numbers': 'Parliament',
      'sponsors': 'Sponsor'
    };
    return names[facetName] || facetName;
  };

  const handlePageChange = (event, newPage) => {
    handleSearch(null, newPage);
  };

  const handleReset = () => {
    setQuery('');
    setForm({
      keywords: '',
      status: '',
      originatingChamber: '',
      billType: '',
      portfolio: '',
      sponsor: '',
      parliamentNumber: '',
      mover: ''
    });
    setResults(null);
    setPage(1);
    setActiveFilters({});
    setChamberFilter(null);
    setSortBy(DEFAULT_SORT);
    setFacets({});
    setSelectedYear(null);
    setSelectedMonth(null);
    setSelectedDay(null);
    setSearchParams({});
  };

  const toggleDetails = (id) => {
    setExpandedDetails(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const truncateContent = (content, maxLength = 300) => {
    if (!content) return '';
    if (content.length <= maxLength) return content;
    return content.substring(0, maxLength) + '...';
  };

  const getStatusColor = (status) => {
    if (!status) return 'default';
    const lowerStatus = status.toLowerCase();
    if (lowerStatus.includes('act') || lowerStatus.includes('passed')) return 'success';
    if (lowerStatus.includes('lapsed') || lowerStatus.includes('negatived')) return 'error';
    if (lowerStatus.includes('before')) return 'warning';
    return 'default';
  };

  const MetadataRow = ({ label, value }) => {
    if (value === null || value === undefined || value === '' || (Array.isArray(value) && value.length === 0)) return null;
    return (
      <TableRow>
        <TableCell sx={{ fontWeight: 500, color: 'text.secondary', width: '30%', py: 1 }}>{label}</TableCell>
        <TableCell sx={{ py: 1 }}>{Array.isArray(value) ? value.join(', ') : String(value)}</TableCell>
      </TableRow>
    );
  };

  const handleModeChange = (event, newMode) => {
    if (newMode !== null) {
      setSearchMode(newMode);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">Bills</Typography>
        <ToggleButtonGroup
          value={searchMode}
          exclusive
          onChange={handleModeChange}
          size="small"
        >
          <ToggleButton value="simple">
            <SearchIcon sx={{ mr: 1 }} /> Simple
          </ToggleButton>
          <ToggleButton value="advanced">
            <TuneIcon sx={{ mr: 1 }} /> Advanced
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Simple Search */}
      <Collapse in={searchMode === 'simple'}>
        <Paper elevation={1} sx={{ p: 2, mb: 4, borderRadius: 3 }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              fullWidth
              placeholder="Search bills..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              variant="outlined"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
            />
            <Button
              variant="contained"
              onClick={() => handleSearch()}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
              sx={{ borderRadius: 3, px: 4 }}
            >
              Search
            </Button>
            <Button variant="outlined" onClick={handleReset} sx={{ borderRadius: 3 }}>
              Reset
            </Button>
          </Box>
        </Paper>
      </Collapse>

      {/* Advanced Search */}
      <Collapse in={searchMode === 'advanced'}>
        <Paper elevation={1} sx={{ p: 3, mb: 4, borderRadius: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Keywords"
                value={form.keywords}
                onChange={handleChange('keywords')}
                placeholder="Search in bill title and content"
                size="small"
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select value={form.status} onChange={handleChange('status')} label="Status">
                  <MenuItem value="">All Statuses</MenuItem>
                  <MenuItem value="Act">Act</MenuItem>
                  <MenuItem value="Before House of Reps">Before House of Reps</MenuItem>
                  <MenuItem value="Before Senate">Before Senate</MenuItem>
                  <MenuItem value="Passed Both Houses">Passed Both Houses</MenuItem>
                  <MenuItem value="Lapsed">Lapsed</MenuItem>
                  <MenuItem value="Negatived">Negatived</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Originating Chamber</InputLabel>
                <Select value={form.originatingChamber} onChange={handleChange('originatingChamber')} label="Originating Chamber">
                  <MenuItem value="">All Chambers</MenuItem>
                  <MenuItem value="House of Reps">House of Representatives</MenuItem>
                  <MenuItem value="Senate">Senate</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Bill Type</InputLabel>
                <Select value={form.billType} onChange={handleChange('billType')} label="Bill Type">
                  <MenuItem value="">All Types</MenuItem>
                  <MenuItem value="Government">Government</MenuItem>
                  <MenuItem value="Private Member">Private Member</MenuItem>
                  <MenuItem value="Private Senator">Private Senator</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Autocomplete
                fullWidth
                size="small"
                options={availableOptions.portfolios}
                value={form.portfolio}
                onChange={(e, v) => setForm({...form, portfolio: v || ''})}
                renderInput={(params) => <TextField {...params} label="Portfolio" placeholder="e.g., Treasury, Health" />}
                freeSolo
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Autocomplete
                fullWidth
                size="small"
                options={availableOptions.sponsors}
                value={form.sponsor}
                onChange={(e, v) => setForm({...form, sponsor: v || ''})}
                renderInput={(params) => <TextField {...params} label="Sponsor" placeholder="Bill sponsor name" />}
                freeSolo
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Autocomplete
                fullWidth
                size="small"
                options={availableOptions.parliamentNumbers.map(String)}
                value={form.parliamentNumber}
                onChange={(e, v) => setForm({...form, parliamentNumber: v || ''})}
                renderInput={(params) => <TextField {...params} label="Parliament Number" placeholder="e.g., 47" />}
                freeSolo
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Autocomplete
                fullWidth
                size="small"
                options={availableOptions.movers}
                value={form.mover}
                onChange={(e, v) => setForm({...form, mover: v || ''})}
                renderInput={(params) => <TextField {...params} label="Mover" placeholder="Bill mover name" />}
                freeSolo
              />
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <Button
                  variant="contained"
                  onClick={() => handleSearch()}
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
                  sx={{ borderRadius: 2 }}
                >
                  Search
                </Button>
                <Button variant="outlined" onClick={handleReset} sx={{ borderRadius: 2 }}>
                  Reset
                </Button>
                <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                  Query: <code>{buildAdvancedQuery() || '(empty)'}</code>
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Collapse>

      {results && (
        <Grid container spacing={3}>
          {/* Facets Panel */}
          {Object.keys(facets).length > 0 && (
            <Grid item xs={12} md={3}>
              <Paper elevation={1} sx={{ p: 2, borderRadius: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
                    Filter Results
                  </Typography>
                  {(Object.keys(activeFilters).length > 0 || chamberFilter || selectedYear) && (
                    <Button size="small" onClick={() => { clearAllFilters(); clearDateDrillDown(); }}>Clear All</Button>
                  )}
                </Box>

                {/* Date Hierarchy Filter */}
                {(facets.years?.length > 0 || facets.months?.length > 0 || facets.days?.length > 0) && (
                  <DateHierarchyFilter
                    facets={facets}
                    selectedYear={selectedYear}
                    selectedMonth={selectedMonth}
                    selectedDay={selectedDay}
                    onYearClick={handleYearClick}
                    onMonthClick={handleMonthClick}
                    onDayClick={handleDayClick}
                    onClearAll={clearDateDrillDown}
                  />
                )}

                {FACET_ORDER.map((facetName) => (
                  facets[facetName] && facets[facetName].length > 0 && (
                    <Accordion key={facetName} defaultExpanded={false}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="subtitle2">
                          {facetDisplayName(facetName)}
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails sx={{ pt: 0 }}>
                        <List dense sx={{ py: 0 }}>
                          {facets[facetName].slice(0, 15).map((facet) => {
                            // #251: chamber facet → canonical id route (variant-expansion).
                            if (facetName === 'originating_chambers') {
                              const id = CHAMBER_LABEL_TO_ID[String(facet.value)];
                              return (
                                <ListItem key={facet.value} disablePadding>
                                  <Checkbox
                                    size="small"
                                    checked={chamberFilter === id}
                                    onChange={() => handleChamberFacetClick(String(facet.value))}
                                  />
                                  <ListItemText
                                    primary={`${facet.value} (${facet.count.toLocaleString()})`}
                                    primaryTypographyProps={{ variant: 'body2' }}
                                  />
                                </ListItem>
                              );
                            }
                            return (
                              <ListItem key={facet.value} disablePadding>
                                <Checkbox
                                  size="small"
                                  checked={activeFilters[facetToField(facetName)]?.includes(String(facet.value)) || false}
                                  onChange={() => handleFacetClick(facetToField(facetName), String(facet.value))}
                                />
                                <ListItemText
                                  primary={`${facetName === 'parliament_numbers' ? `Parliament ${facet.value}` : facet.value} (${facet.count.toLocaleString()})`}
                                  primaryTypographyProps={{ variant: 'body2' }}
                                />
                              </ListItem>
                            );
                          })}
                        </List>
                      </AccordionDetails>
                    </Accordion>
                  )
                ))}
              </Paper>
            </Grid>
          )}

          {/* Results */}
          <Grid item xs={12} md={Object.keys(facets).length > 0 ? 9 : 12}>
            <Paper elevation={1} sx={{ p: 2, borderRadius: 3 }}>
              {/* #251 — Std results layout. Row 1: count (left) + pagination (right). */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="subtitle1">
                  {results.total.toLocaleString()} {results.total === 1 ? 'bill' : 'bills'} found
                </Typography>
                {results.total > perPage && (
                  <Pagination
                    count={Math.ceil(results.total / perPage)}
                    page={page}
                    onChange={handlePageChange}
                    color="primary"
                    size="small"
                  />
                )}
              </Box>
              {/* Row 2: Per page · Sort · Export · Save · RSS */}
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
                <PageSizeSelector value={perPage} onChange={handlePageSizeChange} />
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel id="bills-sort-label">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <SortIcon fontSize="small" />
                      Sort by
                    </Box>
                  </InputLabel>
                  <Select
                    labelId="bills-sort-label"
                    value={sortBy}
                    onChange={(e) => handleSortChange(e.target.value)}
                    label="Sort by"
                  >
                    {SORT_OPTIONS.map(opt => (
                      <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <ExportCSVButton
                  dataType="bills"
                  query={query || '*'}
                  totalResults={results.total}
                />
                <SaveSearchButton
                  searchType="bills"
                  queryText={query || '*'}
                  parameters={{ sort: sortBy, chamber: chamberFilter, page, per_page: perPage, ...activeFilters }}
                  resultCount={results.total}
                />
                <RSSButton collection="bills" query={query || '*'} />
              </Box>
              <List>
                {results.hits.map((hit, index) => (
                  <React.Fragment key={hit.id}>
                    {index > 0 && <Divider />}
                    <ListItem alignItems="flex-start" sx={{ flexDirection: 'column' }}>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                            <Typography
                              variant="resultTitle"
                              component={RouterLink}
                              to={`/bills/${hit.id}`}
                              sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                            >
                              {hit.title || 'Untitled Bill'}
                            </Typography>
                            <Chip
                              label={hit.status || 'Unknown'}
                              size="small"
                              color={getStatusColor(hit.status)}
                            />
                            {hit.originating_chamber && (
                              <Chip
                                label={hit.originating_chamber}
                                size="small"
                                variant="outlined"
                                color={hit.originating_chamber === 'Senate' ? 'error' : 'primary'}
                              />
                            )}
                          </Box>
                        }
                        secondary={
                          <Box sx={{ mt: 1 }}>
                            {hit.portfolio && (
                              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                                Portfolio: {hit.portfolio}
                              </Typography>
                            )}
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                              {[
                                hit.bill_type && `${hit.bill_type} Bill`,
                                hit.parliament_number && `Parliament ${hit.parliament_number}`,
                                hit.last_updated && `Updated: ${formatDate(hit.last_updated)}`
                              ].filter(Boolean).join(' · ')}
                            </Typography>
                            {hit.sponsors && hit.sponsors.length > 0 && (
                              <Typography variant="body2" color="text.secondary">
                                Sponsors: {hit.sponsors.join(', ')}
                              </Typography>
                            )}
                            <Typography className="ps-snippet" variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                              {hit._highlights?.content ? (
                                <span dangerouslySetInnerHTML={{
                                  __html: hit._highlights.content.join(' ... ')
                                }} />
                              ) : (
                                truncateContent(hit.content)
                              )}
                            </Typography>
                          </Box>
                        }
                      />
                      <Button size="small" onClick={() => toggleDetails(hit.id)} sx={{ mt: 1 }}>
                        {expandedDetails[hit.id] ? 'Hide details' : 'Show all details'}
                      </Button>
                      <Collapse in={expandedDetails[hit.id]}>
                        <Box sx={{ mt: 2, width: '100%' }}>
                          <Table size="small">
                            <TableBody>
                              <TableRow>
                                <TableCell colSpan={2} sx={{ bgcolor: '#f5f5f5', fontWeight: 600 }}>
                                  Bill Information
                                </TableCell>
                              </TableRow>
                              <MetadataRow label="Title" value={hit.title} />
                              <MetadataRow label="Bill ID" value={hit.bill_id} />
                              <MetadataRow label="Status" value={hit.status} />
                              <MetadataRow label="Bill Type" value={hit.bill_type} />
                              <MetadataRow label="Originating Chamber" value={hit.originating_chamber} />
                              <MetadataRow label="Portfolio" value={hit.portfolio} />
                              <MetadataRow label="Parliament" value={hit.parliament_number} />
                              <MetadataRow label="Sponsors" value={hit.sponsors} />
                              <MetadataRow label="Movers" value={hit.movers} />
                              <MetadataRow label="Last Updated" value={formatDate(hit.last_updated)} />
                              <TableRow>
                                <TableCell colSpan={2} sx={{ bgcolor: '#f5f5f5', fontWeight: 600 }}>
                                  Document Availability
                                </TableCell>
                              </TableRow>
                              <MetadataRow label="Bills Digest" value={hit.has_bills_digest ? 'Yes' : 'No'} />
                              <MetadataRow label="Explanatory Memorandum" value={hit.has_explanatory_memorandum ? 'Yes' : 'No'} />
                              <MetadataRow label="Text of Bill" value={hit.has_text_of_bill ? 'Yes' : 'No'} />
                              <MetadataRow label="Second Reading Speech" value={hit.has_second_reading_speech ? 'Yes' : 'No'} />
                              <MetadataRow label="Related Documents" value={hit.document_count} />
                              <MetadataRow label="Amendments" value={hit.amendment_count} />
                              <MetadataRow label="Progress Stages" value={hit.progress_stage_count} />
                            </TableBody>
                          </Table>
                          {hit.content && (
                            <Paper sx={{ p: 2, mt: 2, bgcolor: '#fafafa', maxHeight: 400, overflow: 'auto' }}>
                              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                                Content
                              </Typography>
                              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                {hit.content}
                              </Typography>
                            </Paper>
                          )}
                          {hit.parlinfo_url && (
                            <Box sx={{ mt: 2 }}>
                              <Button
                                size="small"
                                variant="outlined"
                                href={hit.parlinfo_url}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                View on ParlInfo
                              </Button>
                            </Box>
                          )}
                        </Box>
                      </Collapse>
                    </ListItem>
                  </React.Fragment>
                ))}
              </List>
              {results.total > perPage && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <Pagination
                    count={Math.ceil(results.total / perPage)}
                    page={page}
                    onChange={handlePageChange}
                    color="primary"
                  />
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      )}
    </Box>
  );
}
